/**
 * Type definition for user roles.
 * Can be one of the predefined roles or any custom string.
 *
 * Aligned with the backend enum (`USER` / `ADMIN` / `PLATFORM_ADMIN`, …)
 * which we always normalize to lowercase on the frontend.
 */
export type UserRole = 'admin' | 'platform_admin' | 'manager' | 'user' | 'rh' | string;

/**
 * Check if the user has *at least one* of the accepted roles.
 *
 * @param {UserRole[]} roles - The list of user roles
 * @param {UserRole[]} acceptedRoles - The roles accepted
 * @returns {boolean} - True if the user has any of the accepted roles
 */
export const hasAnyRole = (roles: UserRole[] = [], acceptedRoles: UserRole[] = []): boolean =>
  acceptedRoles.some(role => roles.includes(role));

/**
 * Le backend sérialise les rôles en majuscules (`USER`, `PLATFORM_ADMIN`).
 * Sur le frontend on les uniformise en minuscules pour `isAdmin`, `hasAnyRole`, etc.
 */
export function normalizeUserRoles(roles: readonly string[] | undefined): UserRole[] {
  return (roles ?? []).map(role => role.toLowerCase()) as UserRole[];
}

/**
 * Check if the user has the `platform_admin` (super-admin) role.
 */
export const isPlatformAdmin = (roles: UserRole[] = []): boolean =>
  roles.includes('platform_admin');

/**
 * Check if the user has admin-level access (`admin` or `platform_admin`).
 *
 * `platform_admin` is a super-admin role on the backend and should always
 * satisfy any `isAdmin` check on the frontend (navigation gates, UI, …).
 */
export const isAdmin = (roles: UserRole[] = []): boolean =>
  roles.includes('admin') || isPlatformAdmin(roles);

/**
 * Check if the user has the `manager` role.
 *
 * @param {UserRole[]} roles - The list of user roles
 * @returns {boolean} - True if the user is a manager
 */
export const isManager = (roles: UserRole[] = []): boolean => roles.includes('manager');

/**
 * Check if the user has the `user` role.
 *
 * @param {UserRole[]} roles - The list of user roles
 * @returns {boolean} - True if the user is a standard user
 */
export const isUser = (roles: UserRole[] = []): boolean => roles.includes('user');

/**
 * Check if the user has the `rh` (HR) role.
 * Accepts both `rh` and `responsable_rh`.
 *
 * @param {UserRole[]} roles - The list of user roles
 * @returns {boolean} - True if the user is an HR
 */
export const isRH = (roles: UserRole[] = []): boolean =>
  roles.includes('rh') || roles.includes('responsable_rh');

/**
 * Rôles qui donnent accès au **portail admin** (back-office). Un compte au seul
 * rôle `user` (consultant / employé) n'y a pas accès. Source unique pour lister
 * « les utilisateurs à accès admin ». `responsable_rh` est un alias de `rh`.
 */
export const ADMIN_PORTAL_ROLES: UserRole[] = [
  'admin',
  'platform_admin',
  'rh',
  'responsable_rh',
  'manager',
];
// Note : l'Observateur (rôle RBAC lecture seule) entre via son rôle enum (ex. ADMIN) —
// il n'a pas de rôle enum « observer ». Le gating de ses actions se fait sur les
// permissions (can(*.manage) = false), pas sur l'identité du rôle. Voir [[rbac-org-permissions]].

/**
 * L'utilisateur a-t-il accès au portail admin ?
 *
 * **Deux voies, et la seconde est devenue la principale.** Le rôle hérité ne
 * gouverne presque plus rien : côté API il est *dérivé des permissions*, et
 * seules `*`, `account.manage`, `role.manage` donnent ADMIN, `timesheet.validate`
 * et `expense.validate` donnent RH. Tout le reste retombe sur USER.
 *
 * Un tenant qui crée un rôle « Observateur » — lecture seule sur les CRA, aucune
 * permission de gestion — obtient donc des comptes au rôle hérité USER, dont
 * l'accès back-office est pourtant légitime. Ne regarder que `roles` les
 * refoulerait vers le portail consultant, et le refus serait d'autant plus
 * déroutant que le rôle vient d'être créé exprès pour eux.
 *
 * Les permissions tranchent sans ambiguïté : `AccountRole.USER` résout sur une
 * liste **vide**, et un compte sans rôle RBAC assigné n'en a aucune. Une liste
 * non vide signifie donc « quelqu'un lui a donné un rôle », jamais « consultant ».
 *
 * Les deux voies sont gardées : sous `RBAC_ENFORCEMENT=off`, un administrateur
 * légitime peut voir arriver `permissions: []` — son rôle enum le rattrape.
 */
export const hasAdminAccess = (roles: UserRole[] = [], permissions?: string[] | null): boolean =>
  hasAnyRole(roles, ADMIN_PORTAL_ROLES) || (permissions?.length ?? 0) > 0;

/**
 * Check if the user has *all* of the required roles.
 *
 * @param {UserRole[]} roles - The list of user roles
 * @param {UserRole[]} requiredRoles - The roles required
 * @returns {boolean} - True if the user has all required roles
 */
export const hasRoles = (roles: UserRole[] = [], requiredRoles: UserRole[] = []): boolean =>
  requiredRoles.every(role => roles.includes(role));
